import { useState } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import AttendeeSignup from "./pages/AttendeeSignup";
import VolunteerSignup from "./pages/VolunteerSignup";
import Register from "./pages/Register";
import Login from "./pages/Login";
import useUser from "./hooks/useUser";

import Resources from "./pages/Resources";
import ResourceDetail from "./pages/ResourceDetail";
import Contact from "./pages/Contact";
import About from "./pages/About";

/* ---------- Page Transition ---------- */
function PageTransition({ children }) {
  const prefersReduced = useReducedMotion();

  const variants = {
    initial: { opacity: 0, y: prefersReduced ? 0 : 8, scale: prefersReduced ? 1 : 0.995 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: prefersReduced ? 0 : -8, scale: prefersReduced ? 1 : 0.995 },
  };

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="will-change-transform"
    >
      {children}
    </motion.div>
  );
}

/* ---------- Main App ---------- */
export default function App() {
  const [navOpen, setNavOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useUser();

  const openLogoutModal = () => {
    setShowLogoutModal(true);
  };

  const closeLogoutModal = () => {
    setShowLogoutModal(false);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-ash text-ink font-sans">
      {/* Skip link for accessibility */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:bg-white focus:px-3 focus:py-2 focus:rounded-lg focus:shadow"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <img
              src="/wis-logo.jpg"
              alt="Maynooth University — Women & Girls in STEM Club"
              className="h-12 w-auto object-contain"
            />
            <span className="text-xl sm:text-2xl font-semibold">
              Women and Girls in STEM Club
            </span>
          </div>

          {/* Desktop navigation */}
          <nav aria-label="Primary" className="hidden md:flex items-center gap-6 text-sm">
            <NavItem to="/">Home</NavItem>
            <NavItem to="/signup/attendee">Sign-up (Attendee)</NavItem>
            <NavItem to="/signup/volunteer">Sign-up (Volunteer)</NavItem>
            <NavItem to="/resources">Resources</NavItem>
            <NavItem to="/about">About</NavItem>
            <NavItem to="/contact">Contact</NavItem>

            {/* Right Auth Buttons */}
            <div className="ml-auto flex items-center gap-3">
              {user ? (
                <>
                  <span className="text-sm font-medium text-ink">
                    Welcome back, {user.name.split(" ")[0]} 
                  </span>

                  <button
                    type="button"
                    onClick={openLogoutModal}
                    className="rounded-xl border border-brand-300 text-brand-700 px-3 py-1 hover:bg-brand-50"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-sm font-medium text-brand-700 hover:underline"
                  >
                    Log in
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/register")}
                    className="rounded-xl bg-brand-600 text-white px-3 py-1 font-semibold hover:bg-brand-700"
                  >
                    Register
                  </button>
                </>
              )}
            </div>
          </nav>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden rounded-lg p-2 border focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
            aria-expanded={navOpen}
            aria-controls="mobile-nav"
            onClick={() => setNavOpen((v) => !v)}
            title="Toggle menu"
            type="button"
          >
            <span className="sr-only">Toggle menu</span>
            <span aria-hidden="true">☰</span>
          </button>
        </div>

        {/* Mobile panel for mobile phones */}
        {navOpen && (
          <div id="mobile-nav" className="md:hidden border-t">
            <nav aria-label="Mobile" className="px-4 py-3 grid gap-2 text-sm">
              <NavItem to="/" onClick={() => setNavOpen(false)}>
                Home
              </NavItem>
              <NavItem to="/signup/attendee" onClick={() => setNavOpen(false)}>
                Sign-up (Attendee)
              </NavItem>
              <NavItem to="/signup/volunteer" onClick={() => setNavOpen(false)}>
                Sign-up (Volunteer)
              </NavItem>
              <NavItem to="/resources" onClick={() => setNavOpen(false)}>
                Resources
              </NavItem>
              <NavItem to="/about" onClick={() => setNavOpen(false)}>
                About
              </NavItem>
              <NavItem to="/contact" onClick={() => setNavOpen(false)}>
                Contact
              </NavItem>

              {/* Mobile Auth Buttons */}
              <div className="flex flex-col gap-2 pt-2">
                {user ? (
                  <>
                    <span className="text-sm font-medium text-ink">
                      Welcome back, {user.name.split(" ")[0]} 👋
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        setNavOpen(false);
                        openLogoutModal();
                      }}
                      className="rounded-xl border border-brand-300 text-brand-700 px-3 py-2 hover:bg-ash"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setNavOpen(false);
                        navigate("/login");
                      }}
                      className="rounded-xl border px-3 py-2"
                    >
                      Log in
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setNavOpen(false);
                        navigate("/register");
                      }}
                      className="rounded-xl bg-brand-500 text-white px-3 py-2 font-medium hover:bg-brand-600"
                    >
                      Register
                    </button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Routed Content */}
      <main id="main" className="min-h-[60vh]">
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route
              path="/"
              element={
                <PageTransition>
                  <Home />
                </PageTransition>
              }
            />
            <Route
              path="/signup/attendee"
              element={
                <PageTransition>
                  <AttendeeSignup />
                </PageTransition>
              }
            />
            <Route
              path="/signup/volunteer"
              element={
                <PageTransition>
                  <VolunteerSignup />
                </PageTransition>
              }
            />
            <Route
              path="/resources"
              element={
                <PageTransition>
                  <Resources />
                </PageTransition>
              }
            />
            <Route
              path="/resources/:slug"
              element={
                <PageTransition>
                  <ResourceDetail />
                </PageTransition>
              }
            />
            <Route
              path="/about"
              element={
                <PageTransition>
                  <About />
                </PageTransition>
              }
            />
            <Route
              path="/contact"
              element={
                <PageTransition>
                  <Contact />
                </PageTransition>
              }
            />
            <Route
              path="/register"
              element={
                <PageTransition>
                  <Register />
                </PageTransition>
              }
            />
            <Route
              path="/login"
              element={
                <PageTransition>
                  <Login />
                </PageTransition>
              }
            />
          </Routes>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t">
        <div className="max-w-6xl mx-auto px-4 py-6 text-sm text-ink/70">
          © {new Date().getFullYear()} Women and Girls in STEM Club · Maynooth University
        </div>
      </footer>

      {/* Logout Confirmation */}
      {showLogoutModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-6">
            <h2 className="text-xl font-semibold text-ink mb-2">Log out?</h2>
            <p className="text-sm text-ink/70 mb-6">
              Are you sure you want to log out of your account?
            </p>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeLogoutModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Go back
              </button>

              <button
                type="button"
                onClick={confirmLogout}
                className="px-4 py-2 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-600"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Components ---------- */

function NavItem({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-600 ${
          isActive ? "font-semibold underline" : ""
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function Home() {
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero Section */}
      <section
        aria-labelledby="hero-title"
        className="text-center text-ink"
        style={{
          background: "linear-gradient(90deg, #ffc8d8 0%, #fba7c0 50%, #ffe4ec 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-20">
          <h1 id="hero-title" className="text-4xl sm:text-5xl font-extrabold text-ink">
            Women & Girls in STEM After-School Club
          </h1>
          <p className="mt-3 text-lg sm:text-xl text-ink/80">
            A friendly, hands-on space to explore STEM.
          </p>

          <div className="mt-6 inline-flex gap-3">
            <button
              onClick={() => navigate("/signup/attendee")}
              className="rounded-2xl bg-brand-500 text-white font-semibold px-6 py-3 hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500"
            >
              Register an Attendee
            </button>
            <button
              onClick={() => navigate("/signup/volunteer")}
              className="rounded-2xl bg-white text-ink px-6 py-3 border hover:bg-ash focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500"
            >
              Volunteer Sign-up
            </button>
          </div>

          <p className="mt-4 text-sm text-ink/80">
            🕕 6:00–7:30 PM · 📍 Eolas Building (North Campus)
          </p>
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <div className="grid md:grid-cols-3 gap-8">
          <Card
            title="Volunteer Sign-up"
            copy="Help us run engaging STEM sessions and be a role model."
            cta="Apply Now →"
            onClick={() => navigate("/signup/volunteer")}
          />
          <Card
            title="Attendee Registration"
            copy="Weekly activities with coding, electronics, and creative problem-solving."
            cta="Sign Up →"
            onClick={() => navigate("/signup/attendee")}
          />
          <Card
            title="Learning Resources"
            copy="Free tutorials and take-home activities to keep learning."
            cta="Explore →"
            onClick={() => navigate("/resources")}
          />
        </div>
      </section>
    </div>
  );
}

function Card({ title, copy, cta, onClick }) {
  return (
    <section
      className="bg-white rounded-2xl shadow-soft border p-6 hover:shadow-md transition-shadow"
      aria-labelledby={slugify(title)}
    >
      <h2 id={slugify(title)} className="text-xl font-semibold">
        {title}
      </h2>
      <p className="mt-2 text-ink/80">{copy}</p>
      <button
        onClick={onClick}
        className="mt-4 inline-flex items-center gap-2 text-brand-700 font-medium underline decoration-2 underline-offset-4 hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-600"
      >
        <span>{cta}</span>
      </button>
    </section>
  );
}

function slugify(s) {
  return s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}
